import {compact} from '@benchling/js-utils';

import BenchlingConfig from 'BenchlingConfig';
import {
  StructuredTable,
  StructuredTableAdapter,
  StructuredTableKind,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getSourceTypeForStructuredTable} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/TableConstants';
import {getStorableIdsFromTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {TaskColumnMappingInfo} from 'processes/modals/SelectTaskColumnMappingsModal';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';

import {ProcessOutputStructuredTable} from '../ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from '../ProcessTaskStructuredTableAdapter';

/**
 * Derive candidate source columns and analytics source type for the task-creation shortcut.
 *
 * For each candidate column, we count the number of mappable items across all rows in that column.
 * This is the potential number of tasks we would create from that column (not the number of table columns).
 *
 */
export function getSourceColumnsAndTypeFromStructuredTable(
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>> | null,
  adapter: StructuredTableAdapter<unknown, unknown>
): {sourceColumns: Array<TaskColumnMappingInfo>; sourceType: string} {
  let sourceLocation;
  const sourceColumns: Array<TaskColumnMappingInfo> = [];
  // Determine creation sourceType for product analytics
  if (structuredTable.kind === StructuredTableKind.PROCESS_TASK) {
    sourceLocation = (structuredTable as ProcessTaskStructuredTable).metadata.tableLocation?.toLowerCase();
  } else if (structuredTable.kind === StructuredTableKind.PROCESS_OUTPUT) {
    sourceLocation = (structuredTable as ProcessOutputStructuredTable).metadata.tableLocation?.toLowerCase();
  } else {
    sourceLocation = 'entry';
  }
  const sourceType = `${sourceLocation}_${getSourceTypeForStructuredTable(structuredTable)}_shortcut`;

  const sourceColumnSummaries = adapter.getColumnSummariesForCreateProcessShortcut(structuredTable);
  if (computedTable != null) {
    const sourceColumnIndices = sourceColumnSummaries.map(({columnIndex}) => columnIndex);

    const getMappableItemsFromCell = (
      cellValue: TypedValue
    ): Array<{id: string; type: BaseTypedValueType}> => {
      if (cellValue.type === BaseTypedValueType.ARRAY) {
        return cellValue.rawValue.map((value) => getMappableItemsFromCell(value)).flat();
      } else if (cellValue.type === BaseTypedValueType.BIOENTITY) {
        return [{id: cellValue.bioentitySummary.id, type: BaseTypedValueType.BIOENTITY}];
      } else if (cellValue.type === BaseTypedValueType.STORABLES) {
        return getStorableIdsFromTypedValue(cellValue).map((id) => ({
          id,
          type: BaseTypedValueType.STORABLES,
        }));
      } else if (cellValue.type === FieldType.STORABLE_LINK) {
        return (cellValue.targetStorables ?? []).map(({id}) => ({id, type: BaseTypedValueType.STORABLES}));
      } else if (cellValue.type === FieldType.BIOENTITY_LINK) {
        return compact(
          (cellValue.targetFiles ?? []).map(({id}) => (id ? {id, type: BaseTypedValueType.BIOENTITY} : null))
        );
      } else {
        return [];
      }
    };

    const taskGroupLimit = BenchlingConfig.WORKFLOWS_MAX_TASKS_PER_TASK_GROUP;

    for (const [index, sourceColumnIndex] of sourceColumnIndices.entries()) {
      // Aggregate all mappable items from every row in this column
      const mappableItems = computedTable
        .map((row) => {
          const cellValue = row[sourceColumnIndex].value;
          return getMappableItemsFromCell(cellValue);
        })
        .flat();

      const potentialTaskCount = mappableItems.length;
      const isNonEmpty = potentialTaskCount > 0;
      const isWithinProductLimit = potentialTaskCount < taskGroupLimit;

      if (isNonEmpty && isWithinProductLimit) {
        sourceColumns.push({
          columnName: structuredTable.columns[sourceColumnIndex].name,
          columnIds: mappableItems.map(({id}) => id),
          columnType: mappableItems[0].type,
          hasValues: false,
          shouldFetchPlateInfo: sourceColumnSummaries[index].shouldFetchPlateInfo,
        } as TaskColumnMappingInfo);
      }
    }
  }
  return {sourceColumns, sourceType};
}
